/**
 * Expense Audit Page — Admin tool
 * Upload management invoice PDFs, get AI-extracted audit with flagged findings.
 * View past audits from history.
 */

import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '@/services/authService';
import { API_BASE_URL } from '@/services/api';
import {
  CloudArrowUpIcon,
  DocumentMagnifyingGlassIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  FunnelIcon,
  ClockIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import AuditChatPanel from '@/components/AuditChatPanel';

// ── Types ────────────────────────────────────────────────────────────────────

interface LedgerLine {
  gl_code: string;
  category: string;
  date: string;
  vendor: string;
  source: string;
  amount_cad: number | null;
  amount_usd: number | null;
  has_receipt: boolean | string;
  notes: string;
  flags: string[];
}

interface InvoiceSummary {
  gl_code: string;
  category: string;
  currency: string;
  amount: number;
}

interface AuditResult {
  id?: number;
  period: string;
  aircraft: string;
  invoice_summary: InvoiceSummary[];
  ledger: LedgerLine[];
  findings_count: number;
  total_cad: number;
  total_usd: number;
  created_at?: string;
  created_by?: string;
  source_filenames?: string[];
}

interface AuditListItem {
  id: number;
  period: string;
  aircraft: string;
  total_cad: number;
  total_usd: number;
  line_item_count: number;
  findings_count: number;
  source_filenames: string[];
  created_at: string;
  created_by: string;
}

// ── Component ────────────────────────────────────────────────────────────────

export default function ExpenseAuditPage() {
  const [files, setFiles] = useState<File[]>([]);
  const [processing, setProcessing] = useState(false);
  const [progressStep, setProgressStep] = useState(0);
  const [result, setResult] = useState<AuditResult | null>(null);
  const [error, setError] = useState('');
  const [showFindingsOnly, setShowFindingsOnly] = useState(false);
  const [history, setHistory] = useState<AuditListItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE_URL}/expense-audit`);
      if (res.ok) {
        const data = await res.json();
        setHistory(data.data || []);
      }
    } catch {
      // History fetch is non-critical
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
      setResult(null);
      setError('');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files).filter(f => f.type === 'application/pdf');
    if (dropped.length > 0) {
      setFiles(prev => [...prev, ...dropped]);
      setResult(null);
      setError('');
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const PROGRESS_STEPS = [
    'Uploading PDFs...',
    'Reading invoices & receipts...',
    'Extracting line items with AI...',
    'Applying audit rules...',
    'Saving results...',
  ];

  const handleProcess = async () => {
    if (files.length === 0) return;

    setProcessing(true);
    setProgressStep(0);
    setError('');
    setResult(null);

    // Simulate progress steps while waiting for the API
    const stepTimer = setInterval(() => {
      setProgressStep(prev => Math.min(prev + 1, PROGRESS_STEPS.length - 2));
    }, 8000);

    try {
      const formData = new FormData();
      files.forEach(f => formData.append('pdfs', f));

      setProgressStep(1);

      const response = await authFetch(`${API_BASE_URL}/expense-audit/process`, {
        method: 'POST',
        body: formData,
        headers: {},
      });

      clearInterval(stepTimer);
      setProgressStep(PROGRESS_STEPS.length - 1);

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Processing failed');
      }

      const data = await response.json();
      setResult(data.data);
      fetchHistory();
    } catch (err: any) {
      clearInterval(stepTimer);
      setError(err.message || 'Failed to process invoices');
    } finally {
      setProcessing(false);
      setProgressStep(0);
    }
  };

  const loadAudit = async (id: number) => {
    try {
      const res = await authFetch(`${API_BASE_URL}/expense-audit/${id}`);
      if (!res.ok) throw new Error('Failed to load audit');
      const data = await res.json();
      setResult(data.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load audit');
    }
  };

  const formatCurrency = (amount: number | null, currency: string = 'CAD') => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(amount);
  };

  const displayedLedger = result
    ? showFindingsOnly ? result.ledger.filter(l => l.flags.length > 0) : result.ledger
    : [];

  const exportCsv = () => {
    if (!result) return;

    const escCsv = (val: string) => {
      if (val.includes(',') || val.includes('"') || val.includes('\n')) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    };

    const header = ['GL Code', 'Category', 'Date', 'Vendor', 'Source', 'Amount CAD', 'Amount USD', 'Receipt', 'Flags', 'Notes'];
    const rows = result.ledger.map(l => [
      l.gl_code,
      escCsv(l.category),
      l.date,
      escCsv(l.vendor),
      escCsv(l.source),
      l.amount_cad !== null ? String(l.amount_cad) : '',
      l.amount_usd !== null ? String(l.amount_usd) : '',
      String(l.has_receipt),
      escCsv(l.flags.join('; ')),
      escCsv(l.notes),
    ]);

    const csv = [header.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit_${result.aircraft}_${result.period.replace(/\s+/g, '_')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // ── Upload + History state ──

  if (!result) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
              <DocumentMagnifyingGlassIcon className="w-7 h-7 text-primary" />
              Expense Audit
            </h1>
            <p className="text-muted-foreground mt-1">
              Upload management invoice PDFs for AI-powered expense analysis and flagging
            </p>
          </div>

          {/* Upload card */}
          <div className="bg-card border border-border rounded-xl p-8">
            <label
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              className="block border-2 border-dashed border-border rounded-xl p-12 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-all"
            >
              <CloudArrowUpIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-foreground font-medium mb-1">
                {files.length > 0 ? `${files.length} file${files.length > 1 ? 's' : ''} selected` : 'Drop invoice PDFs here'}
              </p>
              <p className="text-sm text-muted-foreground">
                Management invoice, expense detail, and backup receipts
              </p>
              <input
                type="file"
                accept="application/pdf"
                multiple
                onChange={handleFileChange}
                className="hidden"
              />
            </label>

            {files.length > 0 && (
              <div className="mt-4 space-y-2">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-2 bg-muted/50 rounded-lg text-sm">
                    <span className="text-red-500">PDF</span>
                    <span className="text-foreground font-medium flex-1 truncate">{f.name}</span>
                    <span className="text-muted-foreground">{(f.size / 1024).toFixed(0)} KB</span>
                    <button
                      onClick={() => removeFile(i)}
                      className="text-muted-foreground hover:text-red-500 transition-colors text-xs"
                    >
                      remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            {error && (
              <div className="mt-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 rounded-lg text-sm">
                {error}
              </div>
            )}

            {processing ? (
              <div className="mt-6 space-y-3">
                <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${((progressStep + 1) / PROGRESS_STEPS.length) * 100}%` }}
                  />
                </div>
                <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground">
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />
                  {PROGRESS_STEPS[progressStep]}
                </div>
              </div>
            ) : (
              <button
                onClick={handleProcess}
                disabled={files.length === 0}
                className="mt-6 w-full py-3 bg-primary text-primary-foreground font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <DocumentMagnifyingGlassIcon className="w-5 h-5" />
                Run Expense Audit
              </button>
            )}
          </div>

          {/* Audit history */}
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2 mb-4">
              <ClockIcon className="w-5 h-5 text-muted-foreground" />
              Past Audits
            </h2>

            {historyLoading ? (
              <div className="text-sm text-muted-foreground">Loading...</div>
            ) : history.length === 0 ? (
              <div className="text-sm text-muted-foreground bg-card border border-border rounded-lg px-5 py-8 text-center">
                No audits yet. Upload your first invoice PDF above.
              </div>
            ) : (
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-foreground">Period</th>
                      <th className="px-4 py-3 text-left font-medium text-foreground">Aircraft</th>
                      <th className="px-4 py-3 text-right font-medium text-foreground">Items</th>
                      <th className="px-4 py-3 text-right font-medium text-foreground">Findings</th>
                      <th className="px-4 py-3 text-right font-medium text-foreground">Total CAD</th>
                      <th className="px-4 py-3 text-left font-medium text-foreground">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {history.map((audit) => (
                      <tr
                        key={audit.id}
                        onClick={() => loadAudit(audit.id)}
                        className="hover:bg-muted/30 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3 text-foreground font-medium">{audit.period}</td>
                        <td className="px-4 py-3 text-foreground">{audit.aircraft}</td>
                        <td className="px-4 py-3 text-right text-muted-foreground">{audit.line_item_count}</td>
                        <td className="px-4 py-3 text-right">
                          {audit.findings_count > 0 ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-400">
                              {audit.findings_count}
                            </span>
                          ) : (
                            <span className="text-green-600 dark:text-green-400 text-xs">0</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-foreground">{formatCurrency(audit.total_cad)}</td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {new Date(audit.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Results state ──

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
              <DocumentMagnifyingGlassIcon className="w-7 h-7 text-primary" />
              Expense Audit — {result.aircraft}
            </h1>
            <p className="text-muted-foreground mt-1">
              {result.period}
              {result.created_at && (
                <span className="ml-3 text-xs">
                  Audited {new Date(result.created_at).toLocaleDateString()}
                  {result.created_by && ` by ${result.created_by}`}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportCsv}
              className="px-4 py-2 text-sm font-medium border border-border rounded-lg hover:bg-muted transition-colors text-foreground flex items-center gap-1.5"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={() => { setResult(null); setFiles([]); setShowFindingsOnly(false); }}
              className="px-4 py-2 text-sm font-medium border border-border rounded-lg hover:bg-muted transition-colors text-foreground"
            >
              New Audit
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <SummaryCard label="Total CAD" value={formatCurrency(result.total_cad)} />
          <SummaryCard label="Total USD" value={formatCurrency(result.total_usd, 'USD')} />
          <SummaryCard label="Line Items" value={String(result.ledger.length)} />
          <SummaryCard
            label="Findings"
            value={String(result.findings_count)}
            alert={result.findings_count > 0}
          />
        </div>

        {/* Findings banner */}
        {result.findings_count > 0 && (
          <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ExclamationTriangleIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="font-medium text-amber-900 dark:text-amber-100">
                  {result.findings_count} item{result.findings_count !== 1 ? 's' : ''} flagged for review
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  Missing receipts, amount mismatches, or unusual charges
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowFindingsOnly(!showFindingsOnly)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                showFindingsOnly
                  ? 'bg-amber-500 text-white'
                  : 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300'
              }`}
            >
              <FunnelIcon className="w-4 h-4" />
              {showFindingsOnly ? 'Show All' : 'Findings Only'}
            </button>
          </div>
        )}

        {/* Ledger table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-foreground">GL</th>
                  <th className="px-4 py-3 text-left font-medium text-foreground">Category</th>
                  <th className="px-4 py-3 text-left font-medium text-foreground">Date</th>
                  <th className="px-4 py-3 text-left font-medium text-foreground">Vendor</th>
                  <th className="px-4 py-3 text-right font-medium text-foreground">CAD</th>
                  <th className="px-4 py-3 text-right font-medium text-foreground">USD</th>
                  <th className="px-4 py-3 text-left font-medium text-foreground">Receipt</th>
                  <th className="px-4 py-3 text-left font-medium text-foreground">Flags</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {displayedLedger.map((line, i) => {
                  const hasFinding = line.flags.length > 0;
                  return (
                    <tr key={i} className={hasFinding ? 'bg-amber-50/50 dark:bg-amber-900/10' : 'hover:bg-muted/30'}>
                      <td className="px-4 py-2.5 font-mono text-muted-foreground">{line.gl_code}</td>
                      <td className="px-4 py-2.5 text-foreground">{line.category}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{line.date}</td>
                      <td className="px-4 py-2.5 text-foreground max-w-[200px] truncate" title={line.vendor}>{line.vendor}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-foreground">{formatCurrency(line.amount_cad)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-foreground">{formatCurrency(line.amount_usd, 'USD')}</td>
                      <td className="px-4 py-2.5">
                        {line.has_receipt === true ? (
                          <CheckCircleIcon className="w-4 h-4 text-green-500" />
                        ) : line.has_receipt === false ? (
                          <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />
                        ) : (
                          <span className="text-xs text-muted-foreground">partial</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        {hasFinding ? (
                          <div className="space-y-0.5">
                            {line.flags.map((flag, j) => (
                              <span key={j} className="inline-block px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-400 mr-1">
                                {flag}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-green-600 dark:text-green-400">Clear</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Invoice reconciliation */}
        {result.invoice_summary.length > 0 && (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-border">
              <h2 className="font-semibold text-foreground">Invoice Reconciliation</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-foreground">GL</th>
                    <th className="px-4 py-3 text-left font-medium text-foreground">Category</th>
                    <th className="px-4 py-3 text-left font-medium text-foreground">Currency</th>
                    <th className="px-4 py-3 text-right font-medium text-foreground">Invoice Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {result.invoice_summary.map((line, i) => (
                    <tr key={i} className="hover:bg-muted/30">
                      <td className="px-4 py-2.5 font-mono text-muted-foreground">{line.gl_code}</td>
                      <td className="px-4 py-2.5 text-foreground">{line.category}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{line.currency}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-foreground">
                        {formatCurrency(line.amount, line.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Chat panel */}
      {result.id && <AuditChatPanel auditId={result.id} />}
    </div>
  );
}

// ── Summary Card ─────────────────────────────────────────────────────────────

function SummaryCard({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
  return (
    <div className={`rounded-lg p-4 ${
      alert ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-card border border-border'
    }`}>
      <p className={`text-xs font-medium mb-1 ${alert ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`}>{label}</p>
      <p className={`text-2xl font-bold ${alert ? 'text-amber-800 dark:text-amber-300' : 'text-foreground'}`}>{value}</p>
    </div>
  );
}
